// Utilities for parsing compare slugs

/**
 * Parse a single vehicle token from compare slug into brand/model/variant
 * Supports a few heuristics to handle underscores and dashes used in slugs.
 */
export function parseVehicleToken(token: string): {
    detailedSlug: string;
    variantSlug: string;
} {
    const t = (token || "").trim();
    if (!t) {
        return { detailedSlug: "", variantSlug: "" };
    }

    // Split only on the first '+' — everything before is detailedSlug, after is variantSlug
    const plusIndex = t.indexOf("+");

    if (plusIndex === -1) {
        // No '+' found → assume the whole token is detailedSlug, variantSlug is empty
        return { detailedSlug: t, variantSlug: "" };
    }

    const detailedSlug = t.substring(0, plusIndex);
    const variantSlug = t.substring(plusIndex + 1);

    return {
        detailedSlug: detailedSlug.trim(),
        variantSlug: variantSlug.trim(),
    };
}
/**
 * Decodes a URL-encoded vehicle compare slug
 * e.g. "maruti%2Bfronx%2Balpha-turbo-vs-jeep%2Bmeridian%2Blimited-opt-4x"
 * → "maruti+fronx+alpha-turbo-vs-jeep+meridian+limited-opt-4x"
 */
export function decodeCompareSlug(encodedSlug: string): string {
    if (!encodedSlug || typeof encodedSlug !== "string") {
        return "";
    }

    try {
        // decodeURIComponent handles %2B → +, %20 → space, etc.
        return decodeURIComponent(encodedSlug);
    } catch (e) {
        // If somehow malformed, return the original (safe fallback)
        console.warn("Failed to decode slug:", encodedSlug, e);
        return encodedSlug;
    }
}

/**
 * Split a compare slug into vehicle tokens. Example:
 *  kia+syros+htk-opt-diesel-vs-kia+carens+premium-opt-diesel -> ["kia+syros+htk-opt-diesel", "kia+carens+premium-opt-diesel"]
 */
export function splitCompareSlug(slug: string) {
    return slug.split("-vs-").map((s) => s.trim()).filter(Boolean);
}

/**
 * Validate the overall compare slug shape. Returns true for acceptable slugs.
 * Rules enforced:
 * - Must contain between 2 and 4 vehicle tokens (inclusive).
 * - Each token must contain at least one underscore separating the brand from the rest.
 * - Tokens may contain lowercase letters, numbers, underscores and hyphens only.
 * - No empty parts and no leading/trailing separators.
 */
export function validateCompareSlug(slug: string) {
    if (!slug || typeof slug !== "string") return false;
    const tokens = splitCompareSlug(slug);
    if (tokens.length < 2 || tokens.length > 4) return false;
    // Require strict plus-separated parts: brand+model+variant
    for (const t of tokens) {
        const s = t.trim();
        if (!s) return false;
        if (s.includes(" ")) return false; // no spaces allowed
        if (s.includes("++") || s.startsWith("-") || s.endsWith("-") || s.startsWith("+") || s.endsWith("+")) return false;

        const parts = s.split("+");
        // Require at least brand, model and variant (3 parts). This ensures model/variant
        // can include hyphens without ambiguity (e.g., scorpio-n as model).
        if (parts.length < 3) return false;

        // Ensure each part contains only allowed characters (lowercase letters, digits, hyphens)
        const partRe = /^[a-z0-9-]+$/;
        for (const p of parts) {
            if (!p || !partRe.test(p)) return false;
        }
    }

    return true;
}


// Build a URL-safe token as: "detailed_slug+variant_slug"
// Example:
// detailedSlug = "kia-seltos-gtx-plus-diesel-at"
// variant      = "GTX Plus Diesel AT"
// Result:      "kia-seltos-gtx-plus-diesel-at+gtx-plus-diesel-at"
export function buildVehicleToken({
    detailedSlug,
    variant,
}: {
    detailedSlug?: string | null;
    variant?: string | null;
}) {
    // Use detailedSlug exactly as provided (only basic cleanup: trim and lowercase)
    const cleanDetailedSlug = (s?: string | null): string => {
        const raw = (s || "").toString().trim();
        if (!raw) return "";
        return raw.toLowerCase();
    };

    // Sanitize the standalone variant name into a clean slug
    const sanitizeVariant = (s?: string | null): string => {
        const raw = (s || "").toString().trim();
        if (!raw) return "";

        return raw
            .toLowerCase()
            .replace(/[\s/\\]+/g, "-")       // spaces, slashes → hyphen
            .replace(/[^a-z0-9\-_]+/g, "")   // remove invalid characters
            .replace(/-+/g, "-")             // collapse multiple hyphens
            .replace(/^-+|-+$/g, "");        // trim leading/trailing hyphens
    };

    const detailed = cleanDetailedSlug(detailedSlug);
    const variantSlug = sanitizeVariant(variant);

    // Always return in format: detailed_slug+variant_slug
    // Even if one is empty, preserve the "+" for consistency
    return `${detailed}+${variantSlug}`;
}

/**
 * Build a human-readable compare heading from an array of items.
 * Items may be Variant-like ({ brand?.name, model?.name, name }) or simple strings.
 * Rules (as requested):
 *  - 2 cars: "Brand1 Model1 vs Brand2 Model2 – Compare Price, Features, Colours & Specs"
 *  - 3 cars: "Brand1 Model1, Brand2 Model2 & Brand3 Model3 – Compare Price, Features, Colours & Specs"
 *  - 4 cars: "Brand1 Model1 vs Brand2 Model2 vs Brand3 Model3 vs Brand4 Model4 – Compare Price, Features, Colours & Specs"
 */
export function formatCompareHeading(items: Array<any>) {
    const parts = items
        .map((it) => {
            if (!it) return null;
            if (typeof it === "string") return it.trim();
            const brand = it.brand?.name || it.brand || "";
            const model = it.model?.name || it.model || "";
            const name = it.name || "";
            // Prefer explicit brand+model if available, otherwise fallback to name
            const brandModel = `${brand} ${model}`.trim();
            return brandModel || name || null;
        })
        .filter(Boolean) as string[];

    if (parts.length === 0) return "Compare Cars – Compare Price, Features, Colours & Specs";

    let titleBody = "";
    if (parts.length === 1) {
        titleBody = parts[0];
    } else if (parts.length === 2) {
        titleBody = `${parts[0]} vs ${parts[1]}`;
    } else if (parts.length === 3) {
        // Use comma + ampersand for three items as per requested template
        titleBody = `${parts[0]} vs ${parts[1]} vs ${parts[2]}`;

    } else if (parts.length === 3) {
        // Use comma + ampersand for three items as per requested template
        titleBody = `${parts[0]} vs ${parts[1]} vs ${parts[2]}vs ${parts[3]}`;
    } else {
        // 4 or more: join with ' vs '
        titleBody = parts.slice(0, 4).join(" vs ");
    }

    return `${titleBody}`;
}


// ==================== Helper Functions ====================

// Format a list of car names into a natural language string for descriptions
// Examples: 
// [] → "Compare cars. Check price range, colours, specs, features and more to find the best car for your budget at Car Junction."
// ["Kia Seltos"] → "Compare Kia Seltos. Check price range, colours, specs, features and more to find the best car for your budget at Car Junction."
// ["Kia Seltos", "Hyundai Creta"] → "Compare Kia Seltos and Hyundai Creta. Check price range, colours, specs, features and more to find the best car for your budget at Car Junction."
// ["Kia Seltos", "Hyundai Creta", "MG Hector"] → "Compare Kia Seltos, Hyundai Creta, and MG Hector. Check price range, colours, specs, features and more to find the best car for your budget at Car Junction."
// ["Kia Seltos", "Hyundai Creta", "MG Hector", "Tata Harrier"] → "Compare Kia Seltos, Hyundai Creta, MG Hector and Tata Harrier. Check price range, colours, specs, features and more to find the best car for your budget at Car Junction."
export const formatTitle = (arr: string[]): string => {
    if (arr.length === 0) return "Compare Cars";
    if (arr.length === 2) return `${arr[0]} vs ${arr[1]}`;
    if (arr.length === 3) return `${arr[0]}, ${arr[1]} & ${arr[2]}`;
    if (arr.length === 4) return `${arr[0]} vs ${arr[1]} vs ${arr[2]} vs ${arr[3]}`;

    // 5+ cars
    return `${arr.slice(0, 4).join(' vs ')} and more`;
};

// The description is more generic and can be the same for 2, 3 or 4 cars, so we focus on listing the car names in a natural way.
export const formatDescription = (arr: string[]): string => {
    const base = "Check price range, colours, specs, features and more to find the best car for your budget at Car Junction.";

    if (arr.length === 0) {
        return `Compare cars. ${base}`;
    }

    let list: string;
    if (arr.length === 1) list = arr[0];
    else if (arr.length === 2) list = `${arr[0]} and ${arr[1]}`;
    else if (arr.length <= 4) list = `${arr.slice(0, -1).join(', ')}, and ${arr[arr.length - 1]}`;
    else list = `${arr.slice(0, 4).slice(0, -1).join(', ')}, and ${arr[3]} and more`;

    return `Compare ${list}. ${base}`;
};
